import { useCallback, useState } from 'react'

/**
 * Tiny hook for boolean on/off state. Useful for modals, popovers, dropdowns,
 * sidebars — anything with a simple open/close lifecycle.
 *
 * Usage:
 *   const detail = useToggle()
 *   detail.isOpen      // boolean
 *   detail.open()      // set true
 *   detail.close()     // set false
 *   detail.toggle()    // flip
 *
 * All handlers are stable across renders (memoized once at mount), so they
 * can be passed to child components without retriggering effects.
 */
function useToggle(initialValue = false) {
  const [isOpen, setIsOpen] = useState(initialValue)

  const open = useCallback(() => setIsOpen(true), [])
  const close = useCallback(() => setIsOpen(false), [])
  const toggle = useCallback(() => setIsOpen((prev) => !prev), [])

  return { isOpen, open, close, toggle }
}

export default useToggle
